import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@foundation/components/ui/dialog';
import { Button } from '@foundation/components/ui/button';
import { Textarea } from '@foundation/components/ui/textarea';
import {
  Sparkles,
  Loader2,
  FileText,
  Expand,
  Target,
  Minimize2,
  PlusCircle,
  GraduationCap,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

export type ModulationPreset = {
  id: string;
  label: string;
  icon: React.ElementType;
  promptFragment: string;
  description: string;
};

export const MODULATION_PRESETS: ModulationPreset[] = [
  {
    id: 'elaborate',
    label: 'Elaborate',
    icon: Expand,
    promptFragment: 'Elaborate on this section with more detail, examples, and comprehensive coverage.',
    description: 'Add more detail and depth',
  },
  {
    id: 'precise',
    label: 'Make Precise',
    icon: Target,
    promptFragment: 'Make this section more precise and specific. Remove vague language and use exact terminology.',
    description: 'Sharper, more specific language',
  },
  {
    id: 'shorten',
    label: 'Shorten',
    icon: Minimize2,
    promptFragment: 'Shorten this section significantly while preserving all key information. Be concise.',
    description: 'Reduce length, keep essentials',
  },
  {
    id: 'polish',
    label: 'Polish',
    icon: Sparkles,
    promptFragment: 'Polish this section for professional quality. Improve grammar, flow, and readability.',
    description: 'Improve flow and readability',
  },
  {
    id: 'extend',
    label: 'Extend Section',
    icon: PlusCircle,
    promptFragment: 'Extend this section with additional relevant subsections, considerations, and comprehensive coverage of the topic.',
    description: 'Add new subsections and content',
  },
  {
    id: 'formal',
    label: 'More Formal',
    icon: GraduationCap,
    promptFragment: 'Rewrite this section in a more formal, authoritative tone suitable for official policy documentation.',
    description: 'Official, authoritative tone',
  },
];

export function composeRegenerationPrompt(
  selectedPresets: string[],
  customText: string,
): string {
  const presetFragments = selectedPresets
    .map((id) => MODULATION_PRESETS.find((p) => p.id === id)?.promptFragment)
    .filter(Boolean);

  const parts: string[] = [];
  if (presetFragments.length > 0) {
    parts.push(presetFragments.join(' '));
  }
  if (customText.trim()) {
    parts.push(customText.trim());
  }
  return parts.join('\n\n');
}

type RegenerationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionTitle: string;
  onRegenerate: (composedPrompt: string) => void;
  isRegenerating?: boolean;
  sections?: { id: string; title: string }[];
  selectedSectionIndex?: number;
  onSectionChange?: (index: number) => void;
};

export const RegenerationDialog: React.FC<RegenerationDialogProps> = ({
  open,
  onOpenChange,
  sectionTitle,
  onRegenerate,
  isRegenerating = false,
  sections,
  selectedSectionIndex,
  onSectionChange,
}) => {
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
  const [customText, setCustomText] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const hasSectionPicker = sections && sections.length > 1 && onSectionChange !== undefined && selectedSectionIndex !== undefined;
  const displayTitle = hasSectionPicker ? (sections[selectedSectionIndex]?.title ?? sectionTitle) : sectionTitle;

  useEffect(() => {
    if (open) {
      setSelectedPresets([]);
      setCustomText('');
      setShowPreview(false);
    }
  }, [open]);

  const togglePreset = (presetId: string) => {
    setSelectedPresets((prev) =>
      prev.includes(presetId)
        ? prev.filter((id) => id !== presetId)
        : [...prev, presetId],
    );
  };

  const composedPrompt = composeRegenerationPrompt(selectedPresets, customText);
  const canRegenerate = composedPrompt.trim().length > 0;

  const handleRegenerate = () => {
    if (canRegenerate) {
      onRegenerate(composedPrompt);
    }
  };

  const handleCancel = () => {
    if (!isRegenerating) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isRegenerating ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#33b864]" />
            Regenerate Section
          </DialogTitle>
          <DialogDescription>
            Choose a style or provide custom instructions to regenerate this section.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Section being regenerated */}
          <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500 mb-1">Section to regenerate:</p>
              {hasSectionPicker ? (
                <select
                  value={selectedSectionIndex}
                  onChange={(e) => onSectionChange(Number(e.target.value))}
                  disabled={isRegenerating}
                  className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#33b864] disabled:opacity-50"
                >
                  {sections.map((s, i) => (
                    <option key={s.id} value={i}>{s.title}</option>
                  ))}
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-900 truncate">{displayTitle}</p>
              )}
            </div>
          </div>

          {/* Modulation Presets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Modulations
            </label>
            <div className="flex flex-wrap gap-2">
              {MODULATION_PRESETS.map((preset) => {
                const Icon = preset.icon;
                const isSelected = selectedPresets.includes(preset.id);
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => togglePreset(preset.id)}
                    disabled={isRegenerating}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150 border ${
                      isSelected
                        ? 'bg-[#33b864] text-white border-[#33b864] shadow-sm'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-[#33b864] hover:text-[#33b864]'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={preset.description}
                  >
                    <Icon className="h-3 w-3" />
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Custom Instructions
              <span className="text-gray-400 font-normal ml-1">(optional)</span>
            </label>
            <Textarea
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Describe the tone, style, or specific changes you want..."
              className="min-h-[80px] resize-y"
              maxLength={1000}
              disabled={isRegenerating}
            />
            <p className="text-xs text-gray-400 mt-1">
              {customText.length}/1000 characters
            </p>
          </div>

          {/* Composed Prompt Preview */}
          {canRegenerate && (
            <div>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPreview ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {showPreview ? 'Hide' : 'Show'} prompt preview
              </button>
              {showPreview && (
                <div className="mt-1.5 p-2.5 bg-gray-50 rounded-md border border-gray-100 text-xs text-gray-600 whitespace-pre-wrap max-h-[120px] overflow-y-auto">
                  {composedPrompt}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isRegenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegenerate}
            disabled={!canRegenerate || isRegenerating}
            className="bg-[#33b864] hover:bg-[#2a9d54] text-white"
          >
            {isRegenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-1.5" />
                Regenerate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
